import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

const FILTERS = [
  { slug: "all", label: "All Products" },
  { slug: "medication", label: "Medication" },
  { slug: "hydration", label: "Hydration" },
  { slug: "mobility", label: "Mobility" },
  { slug: "appetite", label: "Appetite" },
  { slug: "litter", label: "Litter-Box" },
  { slug: "bundles", label: "Bundles & Kits" },
  { slug: "guides", label: "Care Guides" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const active = params.get("collection") || "all";
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (active !== "all") q.set("collection", active);
    if (search) q.set("search", search);
    if (sort !== "default") q.set("sort", sort);
    api.get(`/products?${q.toString()}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [active, search, sort]);

  const setCollection = (slug) => {
    if (slug === "all") params.delete("collection");
    else params.set("collection", slug);
    setParams(params);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-2">Shop the catalog</h1>
        <p className="text-gray-500">Curated, vet-reviewed solutions for senior and chronic-condition cats.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input data-testid="shop-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
            className="w-full bg-[#F8F9FA] border border-gray-200 rounded-full pl-12 pr-4 py-3 focus:outline-none focus:border-[#FF5C28]" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="shop-sort" className="w-full lg:w-56 rounded-full bg-[#F8F9FA] border-gray-200 py-3 h-auto">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Featured</SelectItem>
            <SelectItem value="price-asc">Price: Low to High</SelectItem>
            <SelectItem value="price-desc">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
        {FILTERS.map((f) => (
          <button key={f.slug} data-testid={`filter-${f.slug}`} onClick={() => setCollection(f.slug)}
            className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${active === f.slug ? "bg-[#FF5C28] text-white" : "bg-[#F8F9FA] text-gray-700 hover:bg-gray-200"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-gray-400" data-testid="shop-empty">No products found.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="shop-grid">
          {products.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
      )}
    </div>
  );
}
